// Entraînement › « Historique » (refonte navigation 21/10, spec §3). Realised sessions ONLY (fixtures SESSIONS + sessions finished
// in Séance, store.doneSessions), never a booking, never a date after the demo today:
// 1 · Assiduité: the real UBM ActivityHeatmap since the first recorded session (one square per day, a tap opens that day);
// 2 · the month (‹ Octobre 2026 ›): synthesis (séances, small groups / perso, time), then « Ma progression » = performance.tsx
//     reused AS IS on the month, then the month's realised sessions;
// 3 · past reservations: the existing #/reservations?vue=historique (filters, detail, coach question), linked, not copied.
// The sessions sheet reuses the Mon suivi session detail (same set grouping, same score text).
import { Button } from "@ubm/ui/components/button";
import { useMemo, useState, type ReactNode } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { ActivityHeatmap } from "@ubm/ui/components/activity-heatmap";
import { CustomSheet } from "./sheet";
import { useStore } from "./store";
import { DEMO_TODAY, capitalize } from "./demo-context";
import { DATA_START, SESSIONS, type DoneExercise, type Session } from "./suivi-fixtures";
import { Performance, PERF_STATE_RE, type PerfState } from "./performance";
import { scoreText, type MetricKey } from "./performance-model";

const d = (iso: string) => new Date(`${iso}T12:00:00`);
const pad = (n: number) => String(n).padStart(2, "0");
const long = (iso: string) => d(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
/** Same grouping as Mon suivi (only consecutive identical sets are grouped); kept local, suivi.tsx is rewritten by another lot. */
const groupSets = (logs: { load?: number; reps: number }[]) =>
  logs.reduce<{ n: number; reps: number; load?: number }[]>((g, x) => {
    const last = g[g.length - 1];
    if (last && last.reps === x.reps && last.load === x.load) last.n += 1; else g.push({ n: 1, reps: x.reps, load: x.load });
    return g;
  }, []);
const setText = (n: number, reps: number, load: number | undefined, unit?: "reps" | "s") =>
  unit === "s" ? `${n} × ${reps} s` : `${n} × ${reps}${load ? ` à ${load} kg` : ""}`;
const exLine = (e: DoneExercise) => e.score && !e.setLogs && e.sets === undefined ? scoreText(e.score) : e.setLogs
  ? groupSets(e.setLogs).map(g => setText(g.n, g.reps, g.load, e.unit)).join(" + ")
  : setText(e.sets ?? 0, e.reps ?? 0, e.load, e.unit);
const duration = (m: number) => m >= 60 ? `${Math.floor(m / 60)} h ${pad(m % 60)}` : `${m} min`;

/** Month range [start, end] clamped to the demo today; offset 0 = current month. */
function monthOf(offset: number) {
  const t = d(DEMO_TODAY); const m = new Date(t.getFullYear(), t.getMonth() + offset, 1);
  const start = `${m.getFullYear()}-${pad(m.getMonth() + 1)}-01`;
  const last = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
  const end = `${m.getFullYear()}-${pad(m.getMonth() + 1)}-${pad(last)}`;
  return { start, end: end > DEMO_TODAY ? DEMO_TODAY : end, current: offset === 0, label: capitalize(m.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })) };
}

type Sheet = { kind: "session"; s: Session } | { kind: "day"; iso: string; list: Session[] };

export function Historique() {
  const shared = useStore();
  const [offset, setOffset] = useState(0);
  const [exercise, setExercise] = useState("Squat gobelet");
  const [metric, setMetric] = useState<MetricKey>("charge");
  const [perfState, setPerfState] = useState<PerfState | null>(() => (window.location.hash.match(PERF_STATE_RE)?.[1] as PerfState | undefined) ?? null);
  const [all, setAll] = useState(false);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const close = () => setSheet(null);

  // Realised sessions only, up to the demo today (no future session can be « done »), unique by id.
  const realised = useMemo(() => {
    const byId = new Map<string, Session>();
    for (const s of [...SESSIONS, ...shared.doneSessions]) if (s.date <= DEMO_TODAY) byId.set(s.id, s);
    return [...byId.values()].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  }, [shared.doneSessions]);
  const first = realised.length ? realised[realised.length - 1]!.date : DEMO_TODAY;
  const heatStart = first < DATA_START ? first : DATA_START;
  const heatDays = useMemo(() => {
    const out: { date: string; count: number }[] = [];
    for (let t = d(heatStart); t <= d(DEMO_TODAY); t.setDate(t.getDate() + 1)) {
      const iso = `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
      out.push({ date: iso, count: realised.filter(s => s.date === iso).length });
    }
    return out;
  }, [realised, heatStart]);

  const m = monthOf(offset);
  const minOffset = (() => { const a = d(heatStart), t = d(DEMO_TODAY); return (a.getFullYear() - t.getFullYear()) * 12 + a.getMonth() - t.getMonth(); })();
  const month = realised.filter(s => s.date >= m.start && s.date <= m.end);
  const groups = month.filter(s => s.kind === "smallgroup").length;
  const minutes = month.reduce((n, s) => n + s.minutes, 0);
  const shown = all ? month : month.slice(0, 4);
  const openDay = (iso: string) => { const list = realised.filter(s => s.date === iso); if (list.length === 1) setSheet({ kind: "session", s: list[0]! }); else if (list.length) setSheet({ kind: "day", iso, list }); };
  const changeMonth = (o: number) => { setOffset(o); setAll(false); };

  const row = (s: Session) => <li key={s.id}><button type="button" className="cc-ent-done" onClick={() => setSheet({ kind: "session", s })}>
    <span className="cc-ent-done-date"><b>{d(s.date).getDate()}</b><small>{d(s.date).toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "")}</small></span>
    <span className="cc-ent-slot-text"><b>{s.title.replace(/^Small group · /, "")}</b><small>{s.kind === "smallgroup" ? "Small group" : "Séance perso"} · {s.time} · {s.minutes} min</small></span>
    <ChevronRight size={16} aria-hidden="true" className="cc-ent-chev" />
  </button></li>;

  return <div className="cc-ent-histo">
    <section className="cc-ent-block" aria-labelledby="ent-assid">
      <div className="cc-sectionhead"><h2 id="ent-assid">Assiduité</h2></div>
      <div className="cc-ent-heat">
        <ActivityHeatmap days={heatDays} label={`Séances réalisées depuis le ${d(heatStart).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`}
          period={`depuis le ${d(heatStart).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`} unit={{ one: "séance", other: "séances" }}
          thresholds={[1, 2, 3]} weekStartsOn={1} locale="fr-FR" onSelectDate={openDay} />
      </div>
    </section>

    <div className="cc-ent-weeknav cc-ent-monthnav">
      <button type="button" className="cc-icon-btn" aria-label="Mois précédent" disabled={offset <= minOffset} onClick={() => changeMonth(offset - 1)}><ChevronLeft size={18} /></button>
      <p><small>{m.current ? `Au ${d(DEMO_TODAY).getDate()} ${d(DEMO_TODAY).toLocaleDateString("fr-FR", { month: "long" })}` : "Mois terminé"}</small><b>{m.label}</b></p>
      <button type="button" className="cc-icon-btn" aria-label="Mois suivant" disabled={offset >= 0} onClick={() => changeMonth(offset + 1)}><ChevronRight size={18} /></button>
    </div>

    <dl className="cc-ent-stats" aria-label={`Synthèse de ${m.label}`}>
      <div><dt>Séances</dt><dd>{month.length}</dd></div>
      <div><dt>Small groups</dt><dd>{groups}</dd></div>
      <div><dt>Perso</dt><dd>{month.length - groups}</dd></div>
      <div><dt>Temps</dt><dd>{month.length ? duration(minutes) : "—"}</dd></div>
    </dl>

    <section className="cc-ent-block" aria-labelledby="ent-prog">
      <div className="cc-sectionhead"><h2 id="ent-prog">Ma progression</h2></div>
      <div className="cc-suivi-pad cc-perf-host">
        <Performance sessions={realised} period={{ start: m.start, end: m.end }} exercise={exercise} onExercise={setExercise} metric={metric} onMetric={setMetric}
          state={perfState} onRetry={() => setPerfState(null)} onOpenSession={s => setSheet({ kind: "session", s })} />
      </div>
    </section>

    <section className="cc-ent-block" aria-labelledby="ent-done">
      <div className="cc-sectionhead"><h2 id="ent-done">Séances réalisées</h2></div>
      {month.length ? <ul className="cc-ent-donelist">{shown.map(row)}</ul> : <p className="cc-ent-empty">Aucune séance réalisée ce mois-ci.</p>}
      {!all && month.length > 4 && <Button variant="ghost" size="sm" className="cc-ent-more" onClick={() => setAll(true)}>Voir les {month.length} séances</Button>}
    </section>

    <button type="button" className="cc-ent-link" onClick={() => { window.location.hash = "#/reservations?vue=historique"; }}>
      <span className="cc-ent-slot-text"><b>Réservations passées</b><small>Réalisées, annulées et absences</small></span>
      <ChevronRight size={16} aria-hidden="true" className="cc-ent-chev" />
    </button>

    {sheet?.kind === "session" && <Frame title={sheet.s.title} onClose={close}>
      <p className="cc-home-p">{capitalize(long(sheet.s.date))} · {sheet.s.time} · {sheet.s.minutes} min</p>
      <p className="cc-ent-sheet-state" data-booked><Check size={14} strokeWidth={2.6} aria-hidden="true" /> {sheet.s.kind === "smallgroup" ? "Présence validée au studio" : "Séance réalisée"}</p>
      {sheet.s.exercises && <ul className="cc-suivi-kv">{sheet.s.exercises.map(e => <li key={e.name}><span>{e.name}</span><b>{exLine(e)}</b></li>)}</ul>}
      {sheet.s.feeling && <p className="cc-home-p">Ressenti : « {sheet.s.feeling} »</p>}
    </Frame>}
    {sheet?.kind === "day" && <Frame title={capitalize(long(sheet.iso))} onClose={close}>
      <ul className="cc-ent-donelist">{sheet.list.map(s => <li key={s.id}><button type="button" className="cc-ent-done" onClick={() => setSheet({ kind: "session", s })}>
        <span className="cc-ent-slot-text"><b>{s.title}</b><small>{s.time} · {s.minutes} min</small></span><ChevronRight size={16} aria-hidden="true" className="cc-ent-chev" /></button></li>)}</ul>
    </Frame>}
  </div>;
}

function Frame({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return <CustomSheet title={title} onClose={onClose} overlayClassName="cc-home-overlay" className="cc-home-sheet">
    {children}
    <p className="cc-home-demo">Prototype : données de démonstration.</p>
  </CustomSheet>;
}

